/**
 * **区域放置的解析** —— 「主题能让工具栏浮动」这件事的落点。
 *
 * ## 三层优先级
 *
 * ```text
 *   ① 内置默认        BUILTIN_REGION_PLACEMENT      跟着代码走（复刻改动前的观感）
 *   ② 主题声明        theme.layout.regions[编辑器][区域]
 *   ③ 用户偏好        AreaLeaf.regions[key]（目前只有「显示 / 隐藏」）
 *   ────────────────────────────────────────────────
 *   ③ > ② > ①        后面的赢
 * ```
 *
 * ⚠️ **用户永远赢**。理由：主题是「作者推荐的观感」，而用户把工具栏
 * 拖到右边是**明确的操作**。主题一换就把用户的选择抹掉，
 * 会让人不敢调布局。
 *
 * ⚠️ 但用户目前只能表达「显隐」（`RegionState` 是 `Record<RegionKey, boolean>`）。
 * 这是**故意**的中间状态：先把「放置」这条通道打通，
 * 等真有人需要「每个 Area 自己记住工具栏在哪边」时，
 * 再往 `AreaLeaf` 上加一个稀疏的放置覆盖 —— 那时才有真实需求来决定
 * 它该长什么样。现在就把字段加上去，只会在 `LayoutDoc` 里留一堆没人读的键。
 *
 * ## ⚠️ `reserveSpace` 是这里最要紧的一条
 *
 * `NpArea.vue` 头部记着上一轮重构的核心收益：Region 是**布局盒子**而不是浮层，
 * 因为浮层会让 `fitView()` 把节点塞进面板底下（真机事故）。
 *
 * 所以浮动有**两档**，且默认走安全的那一档：
 *
 * ```text
 *   reserveSpace: true  （默认）视觉浮起 —— 留白 / 圆角 / 阴影 / 半透明，
 *                              但**仍然占位**，画布拿到的还是真实剩余空间。
 *   reserveSpace: false        真覆盖画布。此时区域会把自己的厚度写进
 *                              `--np-region-inset-*`，画布据此加内边距。
 * ```
 */
import { inject, provide, ref, type InjectionKey, type Ref } from 'vue'
import type { RegionPlacement, ThemeLayout } from '../types/theme'
import type { EditorType, RegionKey } from './areaContract'

/**
 * 内置默认 —— **复刻改动前的观感**，一条不差。
 *
 * ⚠️ 改这里的值等于改所有没装主题的人的界面。加东西之前先问
 * 「这是不是应该由主题说了算」。
 */
export const BUILTIN_REGION_PLACEMENT: Record<RegionKey, RegionPlacement> = {
  header: { visible: true, mode: 'dock', edge: 'top' },
  /* 小地图是 Area 内部的覆盖层，不是布局盒子 —— 位置由 Vue Flow 自己的 prop 管 */
  minimap: { visible: true, mode: 'float', corner: 'br' },
}

/** 解析之后的区域状态 */
export interface ResolvedRegion {
  visible: boolean
  placement: RegionPlacement
  /** 常用字段拍平出来，省得到处 `?? 默认值` */
  mode: 'dock' | 'float'
  edge: 'top' | 'bottom' | 'left' | 'right'
  corner: 'tl' | 'tr' | 'bl' | 'br'
  reserveSpace: boolean
}

/**
 * 合成一个区域的最终放置。
 *
 * @param editor      这个 Area 显示的是哪种编辑器
 * @param key         哪个区域
 * @param userVisible 用户在界面里选的显隐（`AreaLeaf.regions[key]`）
 * @param themeLayout 当前主题的布局声明（没有就传 `undefined`）
 */
export function resolveRegionPlacement(
  editor: EditorType,
  key: RegionKey,
  userVisible: boolean,
  themeLayout?: ThemeLayout,
): ResolvedRegion {
  const base = BUILTIN_REGION_PLACEMENT[key]
  const fromTheme = themeLayout?.regions?.[editor]?.[key]

  const placement: RegionPlacement = { ...base, ...(fromTheme ?? {}) }

  /*
   * ⚠️ 显隐是 `&&` 而不是「用户值覆盖主题值」。
   *
   * 用户能表达的只有「显示 / 隐藏」这一位。如果这里写成覆盖，
   * 用户把工具栏关掉（false）之后，主题再声明 `visible: true` 也开不回来；
   * 反过来主题说 `visible: false` 而用户说 true 时，应该听谁的？
   *
   * 取 `&&` 的语义是：**两边都同意显示才显示**。也就是
   * 「主题可以关掉一个区域，用户可以再关掉一个主题开着的区域，
   * 但谁都不能强行打开对方关掉的东西」——
   * 对一个「推荐观感」来说这是最不容易让人困惑的规则。
   */
  const themeWantsVisible = placement.visible !== false
  const visible = userVisible && themeWantsVisible

  return {
    visible,
    placement,
    mode: placement.mode ?? 'dock',
    edge: placement.edge ?? 'top',
    corner: placement.corner ?? 'tl',
    /*
     * ⚠️ 默认 `true`。想改成 `false` 之前请先读文件头那段关于 `fitView` 的说明 ——
     * 那是真机上出过的事故，不是理论风险。
     */
    reserveSpace: placement.reserveSpace !== false,
  }
}

/** 一次算完一个 Area 的全部区域 */
export function resolveAreaRegions(
  editor: EditorType,
  userRegions: Record<RegionKey, boolean>,
  themeLayout?: ThemeLayout,
): Record<RegionKey, ResolvedRegion> {
  const out = {} as Record<RegionKey, ResolvedRegion>
  for (const key of Object.keys(userRegions) as RegionKey[]) {
    out[key] = resolveRegionPlacement(editor, key, userRegions[key], themeLayout)
  }
  return out
}

/**
 * 区域厚度换算成画布需要让出的内边距。
 *
 * ⚠️ **只对 `reserveSpace: false` 的浮动区域有值** —— 占位的那种
 * 本来就在 flex 里让出了空间，再算一遍会**让两次**，
 * 表现为画布右侧莫名多出一块空白。
 *
 * 这里返回的是**厚度**（`size`），不是实测宽度。真正的实现应当由
 * 区域自己上报实测矩形（见文件头），但现在只有工具栏会浮动、
 * 而它的宽度是主题声明死的，所以先按声明值算 —— 并且明确写在这里，
 * 免得以后有人以为它是实测值。
 */
export function regionInset(region: ResolvedRegion): number {
  if (!region.visible) return 0
  if (region.mode !== 'float' || region.reserveSpace) return 0
  const size = region.placement.size
  return typeof size === 'number' ? size + (region.placement.gap ?? 0) : 0
}

/* ══════════════════════════════════════════════════════════════════
   把主题布局送进 Area 树
   ══════════════════════════════════════════════════════════════════ */

/**
 * ⚠️ 为什么用 provide/inject 而不是逐层传 prop：
 *
 * `NpAreaTree` 是**递归**的，Area 的层级由用户的布局决定（谁都不知道
 * 会嵌套几层）。用 prop 就得在 `NpAreaTree` 里加一个只为了往下传的
 * 参数 —— 那个参数对 `NpAreaTree` 自己毫无意义。
 *
 * ⚠️ 也不能让 `layout/` 直接 import `theme/registry`：
 * `scripts/verify-layers.ts` 钉死了 `layout → 只许 types / layout`。
 * 那个方向是对的（布局层不该知道主题是怎么存的），所以由**宿主**
 * （`Playground.vue`）把值注进来。
 */
export const REGION_THEME_KEY: InjectionKey<Ref<ThemeLayout>> = Symbol('np-region-theme')

/**
 * 没有 provider 时的兜底（比如把 `NpArea` 单独拿出去用）。
 * ⚠️ 必须是**稳定的同一个对象**：每次 `inject` 都新建一个 ref 的话，
 * 依赖它的 computed 会永远重算。
 */
const EMPTY_LAYOUT: Ref<ThemeLayout> = ref({})

export function provideRegionTheme(layout: Ref<ThemeLayout>): void {
  provide(REGION_THEME_KEY, layout)
}

/** 取当前主题的布局声明。没有 provider 时返回空对象（= 全走内置默认） */
export function useRegionTheme(): Ref<ThemeLayout> {
  return inject(REGION_THEME_KEY, EMPTY_LAYOUT)
}
